import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addColorPalettes } from "../redux/reducers/colorPalette";
import {
  setWheelSegments,
  updateSegmentColorPalette,
} from "../redux/reducers/segmentBuilder";
import {
  getWheelId,
  getWheelSegments,
} from "../redux/selectors/segmentBuilderSelectors";
import { generateScheme, savePalette } from "../services/api";
import { getRandomId } from "../utils/helpers";
import useSegmentSettings from "./useSegmentSetting";
import { PALETTE_TYPES } from "../components/SegmentBuilder/Shared/ColorPalette/constants";


export const useSegmentPalettes = () => {
  const dispatch = useDispatch();
  const wheelId = useSelector(getWheelId);
  const wheelData = useSelector(getWheelSegments);
  const { getColor } = useSegmentSettings() || {};
  const inFlightRef = useRef(new Set());

  const updateSegmentColors = async (palette) => {
    const data = [...wheelData];

    const updatedSegments = data.map((segment, ind) => {
      const { color } = getColor(data, ind, palette);
      return {
        ...segment,
        settings: { ...segment.settings, color },
      };
    });

    dispatch(setWheelSegments(updatedSegments));

    await Promise.all(
      updatedSegments.map((segment) =>
        fetchSegmentPalette(segment.settings.color, segment.id)
      )
    );
  };

  /**
   * Fetches a palette based on a given segment color.
   * Deduplicates concurrent calls for the same segment.
   * @param {string} color - The base color for the palette.
   * @param {string|number} segmentId - The ID of the segment.
   */
  const fetchSegmentPalette = useCallback(
    async (color, segmentId) => {
      const key = `${segmentId}-${color}`;
      if (inFlightRef.current.has(key)) return;
      inFlightRef.current.add(key);

      const hexes = [color, null, null, null, null];

      try {
        const response1 = await generateScheme({
          hex: hexes,
          mode: "auto",
          count: 5,
          scheme_count: 1,
          mood: "auto",
        });

        if (response1.data.success) {
          const finalData = transformPaletteData(response1.data.data, color);
          
          dispatch(addColorPalettes({
            paletteType:PALETTE_TYPES.SEGMENT,
            type:"color",
            paletteColors:finalData,
          }))

          const response2 = await savePalette({
            id: typeof finalData[0].id === 'string' ? undefined : finalData[0].id,
            colors: finalData[0].colors,
            palette_name: finalData[0].paletteName,
            wheel_id: wheelId,
            type: 2,
            usage_type: 2,
            tab_type:1,
            metadata: {
              color:color,
            },
          });

          if (response2.data.success) {
            const paletteId = response2.data.data.id;

            dispatch(
              updateSegmentColorPalette({
                palette: response2.data.data,
                paletteId,
                id: segmentId,
              })
            );
          }
        }
      } catch (error) {
        console.error("Error fetching segment palette:", error);
      } finally {
        inFlightRef.current.delete(key);
      }
    },
    [dispatch, wheelId]
  );

  return {
    fetchSegmentPalette,
    updateSegmentColors,
  };
};

/**
 * Transforms palette data into the required format.
 */
const transformPaletteData = (data, color) => {
  return data.map((item, i) => ({
    index: i,
    paletteName: item.palette_name,
    color,
    colors: item.colors.map((color, ind) => ({
      hex: color.hex.value,
      id: getRandomId(),
      selected: false,
      locked: ind === 0, // Lock the first color which is the segment color
      index: ind,
    })),
    id: getRandomId(),
    selected: i === 0,
  }));
};
